import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertProductVariantColor =
  Database['public']['Tables']['product_variant_color']['Insert']
export type ProductVariantColor_ =
  Database['public']['Tables']['product_variant_color']['Row'] & {
    product_image: Database['public']['Tables']['product_image']['Row'][]
  }

export const useProductVariantColorApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()

  const { data: productVariantColors } = useQuery({
    queryKey: [queryKeys.productVariantColors],
    queryFn: async () =>
      await supabase.from('product_variant_color').select('*'),
  })

  const createProductVariantColor = useMutation({
    mutationFn: async (insertProductVariantColor: InsertProductVariantColor) =>
      await supabase
        .from('product_variant_color')
        .insert(insertProductVariantColor)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariantColors],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })
      toast({
        title: 'Color variant created',
        description: 'Color variant created successfully',
      })
    },
  })

  const updateProductVariantColor = useMutation({
    mutationFn: async (insertProductVariantColor: InsertProductVariantColor) =>
      await supabase
        .from('product_variant_color')
        .update(insertProductVariantColor)
        .eq('id', insertProductVariantColor.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariantColors],
      })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productVariants] })

      toast({
        title: 'Product variant color updated',
        description: 'Product variant color updated successfully',
      })
    },
  })

  const deleteProductVariantColor = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('product_variant_color').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariantColors],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.productVariants],
      })
    },
  })

  return {
    productVariantColors,
    createProductVariantColor,
    updateProductVariantColor,
    deleteProductVariantColor,
  }
}
